import { useAuth }        from "../hooks/useAuth";
import { useState }       from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme }       from "../../../shared/contexts/ThemeContext";
import i18n               from "../../../shared/i18n/i18n";
import { AUTH_CSS }       from "../authStyles";

export default function LoginPage() {
  // Al volver de crear el acceso (invitación) o de cambiar la contraseña, la
  // pantalla llega con el usuario y un mensaje en el estado de la navegación.
  const location = useLocation();
  const [aviso] = useState(location.state?.aviso ?? "");
  const [username, setUsername] = useState(location.state?.username ?? "");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [errors, setErrors]     = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading]   = useState(false);
  const { login }         = useAuth();
  const { t }             = useTranslation();
  const { theme, toggleTheme } = useTheme();

  function handleToggleLang() {
    const next = i18n.language === "es" ? "en" : "es";
    i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
  }

  function validate() {
    const e = {};
    if (!username.trim()) e.username = t("auth.usuarioRequerido");
    if (!password)        e.password = t("auth.passwordRequerida");
    if (password.length > 0 && password.length < 4)
                          e.password = t("auth.passwordMinimo");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleLogin(ev) {
    ev.preventDefault();
    setApiError("");
    if (!validate()) return;
    setLoading(true);
    try {
      await login(username, password);
      // Full page redirect so ThemeContext and all other contexts re-initialize
      // with the values written to localStorage during login (theme, rol, etc.)
      // El operador de agua entra directo a su módulo: "/" no tiene nada que
      // pueda ver.
      const rol = localStorage.getItem("rol");
      const destino = rol === "AGUA" ? "/agua" : rol === "SEGURIDAD" ? "/garita" : "/";
      window.location.replace(destino);
    } catch (err) {
      setApiError(err.message ?? t("auth.credencialesInvalidasMsg"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&display=swap"
        rel="stylesheet"
      />

      <style>{AUTH_CSS}</style>

      {/* ── Navbar ── */}
      <nav className="lp-nav">
        <img src="/LOGO.png" alt="Corvian" />
        <span className="lp-nav-word">
          <span className="cor">COR</span>
          <span className="vian">VANA</span>
        </span>

        {/* Language + Theme toggles */}
        <div className="lp-nav-util">
          <button className="lp-nav-btn" onClick={handleToggleLang}
            title={t("header.cambiarIdioma")}>
            {i18n.language === "es" ? "EN" : "ES"}
          </button>
          <button className="lp-nav-btn" onClick={toggleTheme}
            title={theme === "light" ? t("header.temaOscuro") : t("header.temaClaro")}>
            <i className={`fa-solid ${theme === "light" ? "fa-moon" : "fa-sun"}`} />
          </button>
        </div>
      </nav>

      {/* ── Fondo ── */}
      <div className="lp-bg">

        {/* ── Card ── */}
        <div className="lp-card">

          {/* Panel naranja izquierdo */}
          <div className="lp-panel-left">
            <div className="lp-brand-center">
              <div className="lp-logo-box">
                <img src="/LOGO.png" alt="Corvian logo" />
              </div>
              <div className="lp-brand-name">CORVANA</div>
              <div className="lp-brand-sub">{t("auth.gestionCondominios")}</div>
            </div>

            <div className="lp-status">
              <span className="lp-status-dot" />
              <span>{t("auth.estadoActivo")}</span>
            </div>
          </div>

          {/* Panel oscuro derecho — formulario */}
          <div className="lp-panel-right">
            <h2 className="lp-form-title">{t("auth.iniciarSesion")}</h2>
            <p className="lp-form-sub">{t("auth.subtitulo")}</p>

            {aviso && !apiError && (
              <div className="lp-ok">
                <i className="fa-solid fa-circle-check" style={{ marginTop: 2 }} />
                {aviso}
              </div>
            )}

            {apiError && (
              <div className="lp-api-err">
                <i className="fa-solid fa-circle-exclamation" />
                {apiError}
              </div>
            )}

            <form onSubmit={handleLogin} noValidate>

              {/* Usuario */}
              <div className="lp-field">
                <label className="lp-field-label" htmlFor="lp-u">{t("auth.usuario")}</label>
                <div className="lp-field-wrap">
                  <input
                    id="lp-u"
                    type="text"
                    className={`lp-input${errors.username ? " lp-input-err" : ""}`}
                    placeholder={t("auth.placeholderUsuario")}
                    value={username}
                    onChange={e => { setUsername(e.target.value); setErrors(p => ({ ...p, username: "" })); }}
                    disabled={loading}
                    autoComplete="username"
                    autoFocus
                    style={{ paddingRight: 36 }}
                  />
                  <i className="fa-solid fa-user lp-input-icon" />
                </div>
                {errors.username && (
                  <div className="lp-ferr">
                    <i className="fa-solid fa-circle-exclamation" style={{ fontSize: "0.62rem" }} />
                    {errors.username}
                  </div>
                )}
              </div>

              {/* Contraseña */}
              <div className="lp-field">
                <label className="lp-field-label" htmlFor="lp-p">{t("auth.contrasena")}</label>
                <div className="lp-field-wrap">
                  <input
                    id="lp-p"
                    type={showPass ? "text" : "password"}
                    className={`lp-input${errors.password ? " lp-input-err" : ""}`}
                    placeholder="• • • • • • • •"
                    value={password}
                    onChange={e => { setPassword(e.target.value); setErrors(p => ({ ...p, password: "" })); }}
                    disabled={loading}
                    autoComplete="current-password"
                    style={{ paddingRight: 36 }}
                  />
                  <button
                    type="button" className="lp-eye" tabIndex={-1}
                    onClick={() => setShowPass(v => !v)}
                  >
                    <i className={`fa-solid ${showPass ? "fa-eye-slash" : "fa-eye"}`} />
                  </button>
                </div>
                {errors.password && (
                  <div className="lp-ferr">
                    <i className="fa-solid fa-circle-exclamation" style={{ fontSize: "0.62rem" }} />
                    {errors.password}
                  </div>
                )}
              </div>

              <div style={{ textAlign: "right", margin: "-8px 0 16px" }}>
                <Link to="/recuperar" className="lp-link">
                  {t("auth.olvidasteContrasena")}
                </Link>
              </div>

              {/* Submit */}
              <button type="submit" className="lp-btn" disabled={loading}>
                {loading ? (
                  <><i className="fa-solid fa-circle-notch lp-spin" /> {t("auth.verificando")}</>
                ) : (
                  <>{t("auth.continuar")}</>
                )}
              </button>

            </form>

            <div className="lp-form-foot">
              <strong>CORVIAN</strong> © {new Date().getFullYear()} · {t("auth.derechos")}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
